import React from 'react';
import { Link } from 'react-router-dom';
import {
  Compass,
  PenTool,
  FileCheck,
  Building2,
  Eye,
  ClipboardCheck,
  ArrowRight,
  CheckCircle2,
  Users,
  Clock,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';

const Services = () => {
  const iconMap = {
    Compass: Compass,
    PenTool: PenTool,
    FileCheck: FileCheck,
    Building2: Building2,
    Eye: Eye,
    ClipboardCheck: ClipboardCheck
  };

  const processSteps = [
    { step: '01', title: 'Consultation', description: 'Discuss your vision and requirements' },
    { step: '02', title: 'Planning', description: 'Develop detailed plans and designs' },
    { step: '03', title: 'Permitting', description: 'Handle all approvals' },
    { step: '04', title: 'Construction', description: 'Expert execution with updates' },
    { step: '05', title: 'Delivery', description: 'Inspection and handover' }
  ];

  const benefits = [
    { icon: Shield, title: 'Quality Assurance', description: 'Rigorous standards at every phase' },
    { icon: Clock, title: 'On-Time Delivery', description: 'Projects completed as scheduled' },
    { icon: Users, title: 'Expert Team', description: 'Decades of experience' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-28 pb-12 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-4">
              Comprehensive
              <span className="block text-red-500">Construction Solutions</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              From concept to completion, we provide end-to-end construction services
              that ensure your project's success.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => {
              const Icon = iconMap[service.icon] || Building2;
              return (
                <div
                  key={service.id}
                  className="group bg-white rounded-xl p-6 border border-neutral-200 hover:border-red-500/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                    <Icon className="w-6 h-6 text-red-600 group-hover:text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2">{service.title}</h3>
                  <p className="text-neutral-500 text-sm leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-red-600 text-sm font-semibold tracking-widest uppercase">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-2">
              How We Work
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {processSteps.map((item, index) => (
              <div key={index} className="bg-neutral-50 rounded-xl p-4 border border-neutral-200 text-center">
                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center mb-3 mx-auto">
                  <span className="text-white font-bold text-sm">{item.step}</span>
                </div>
                <h3 className="text-sm font-semibold text-neutral-900 mb-1">{item.title}</h3>
                <p className="text-neutral-500 text-xs">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
                The Citiwide
                <span className="block text-red-500">Advantage</span>
              </h2>

              <div className="space-y-3">
                {[
                  'Design-build expertise from concept to completion',
                  'Specialized hospitality construction since 1995',
                  'Public and private sector experience',
                  'Committed to safety, quality, and efficiency'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-neutral-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-neutral-800 rounded-xl p-4 border border-neutral-700 flex items-start gap-4"
                >
                  <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{benefit.title}</h3>
                    <p className="text-neutral-400 text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Specializations */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-red-600 text-sm font-semibold tracking-widest uppercase">
              Specializations
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-2">
              Our Areas of Expertise
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="relative overflow-hidden rounded-xl aspect-[3/4] shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80"
                alt="Hospitality"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/60 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <h3 className="text-xl font-bold text-white mb-1">Hospitality</h3>
                <p className="text-neutral-300 text-sm mb-2">Restaurants, bars, nightclubs</p>
                <p className="text-red-400 font-semibold text-sm">105 Venues Completed</p>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl aspect-[3/4] shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                alt="Commercial"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/60 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <h3 className="text-xl font-bold text-white mb-1">Commercial</h3>
                <p className="text-neutral-300 text-sm mb-2">Offices, medical, educational</p>
                <p className="text-red-400 font-semibold text-sm">100+ Projects Completed</p>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl aspect-[3/4] shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1647025980693-04e6b24a6d78?w=800&q=80"
                alt="Residential"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/60 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <h3 className="text-xl font-bold text-white mb-1">Residential</h3>
                <p className="text-neutral-300 text-sm mb-2">Custom homes, subdivisions</p>
                <p className="text-red-400 font-semibold text-sm">200+ Units Completed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 bg-red-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Ready to Start Your Project?
          </h2>
          <p className="text-red-100 max-w-2xl mx-auto mb-6">
            Let's discuss how our comprehensive services can bring your
            construction vision to life.
          </p>
          <Link to="/contact">
            <Button className="bg-white hover:bg-neutral-100 text-red-600 font-semibold px-6 py-5">
              Get a Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
